import React from "react";
import { Divider, Box, Badge, Button } from "@vapor/react-material";
import { Avatar, ExtendedTypography } from "@vapor/react-extended";
import {
  faWineBottle,
  faArrowTurnUp,
  faAngleUp,
  faAnglesUp,
  faWrench,
  faUser,
  faUtensils,
  faTools,
  faUnlock,
  faCircle
} from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faCircle as faCircleTwo } from "@fortawesome/pro-solid-svg-icons";

import  VaporIcon  from "@vapor/react-icons/VaporIcon";
import Notification from "../../types";

interface NotificationCardProps {
  notification: Notification;
}

export const NotificationCard: React.FC<NotificationCardProps> = ({
  notification
}) => {
  const {
    senderName,
    senderSurname,
    department,
    status,
    read,
    category,
    title,
    description,
    createdAt
  } = notification;
  const departmentIcons: Record<string, IconDefinition> = {
    WineCellar: faWineBottle,
    Maintenance: faWrench,
    HR: faUser,
    Kitchen: faUtensils,
    Engineering: faTools
  };
  const statusIcons: Record<number, IconDefinition> = {
    1: faAngleUp,
    2: faAnglesUp,
    3: faArrowTurnUp
  };

  const showBadge = senderName !== "" && senderSurname !== "";
  const generateInitials = (name: string, surname: string): string => {
    const nameInitial = name.charAt(0).toUpperCase();
    const surnameInitial = surname.charAt(0).toUpperCase();
    return `${nameInitial}${surnameInitial}`;
  };
  const isMessageTooLong = description.length > 100;

  const calculateTimeAgo = (createdAt: string): string => {
    const createdAtDate = new Date(createdAt);
    const currentTime = new Date();
    const timeDifferenceMilliseconds =
      currentTime.getTime() - createdAtDate.getTime();
    const minutesAgo = Math.floor(timeDifferenceMilliseconds / (1000 * 60));

    if (minutesAgo < 60) {
      return `${minutesAgo} min ago`;
    } else if (minutesAgo < 1440) {
      const hoursAgo = Math.floor(minutesAgo / 60);
      return `${hoursAgo} hour${hoursAgo > 1 ? "s" : ""} ago`;
    } else {
      const daysAgo = Math.floor(minutesAgo / 1440);
      return `${daysAgo} day${daysAgo > 1 ? "s" : ""} ago`;
    }
  };
  const timeAgo = calculateTimeAgo(createdAt);

  return (
    <>
      <Divider light />

      <Box
        display="flex"
        sx={{
          borderLeft: status === 3 ? "6px solid red" : "none",
          backgroundColor: read === false ? "#ebeff5" : "none"
        }}
      >
        <Box
          sx={{
            mt: 2,
            ml: 1,
            mb: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}
        >
          {showBadge && (
            <Badge
              badgeContent={
                <VaporIcon
                  size="8px"
                  icon={departmentIcons[department] || faUnlock}
                />
              }
              sx={{
                "&.MuiBadge-root .MuiBadge-badge": {
                  backgroundColor: "#00BFFF"
                }
              }}
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Avatar sx={{ border: "2px solid #00BFFF" }}>
                <ExtendedTypography variant="body">
                  {generateInitials(senderName, senderSurname)}
                </ExtendedTypography>
              </Avatar>
            </Badge>
          )}
          {!showBadge && (
            <Avatar sx={{ border: "2px solid #00BFFF" }}>
              <VaporIcon
                icon={departmentIcons[department] || faCircle}
                size="14px"
              />
            </Avatar>
          )}
          <VaporIcon
            icon={statusIcons[status]}
            size="12px"
            color="critical"
            sx={{ marginTop: "auto" }}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          sx={{ ml: 2, mt: 1.5, minHeight: "100px" }}
        >
          <ExtendedTypography variant="body">
            <b>{title}</b>
          </ExtendedTypography>
          <ExtendedTypography variant="caption" fontSize="12px" sx={{ mt: 1 }}>
            {category === "message" && isMessageTooLong
              ? description.slice(0, 100) + "..."
              : description}
            {isMessageTooLong && category === "message" && (
              <Button color="primary" size="small">
                More
              </Button>
            )}
          </ExtendedTypography>
          {category === "order" && (
            <Box
              sx={{
                mt: 2,
                mb: 1
              }}
            >
              <Button sx={{ mr: "12px" }} variant="outlined" color="primary">
                Accept
              </Button>
              <Button variant="outlined" color="error">
                Decline
              </Button>
            </Box>
          )}

          <Box
            display="flex"
            justifyContent="space-between"
            width={500}
            sx={{ marginTop: "auto", marginBottom: 2 }}
          >
            <ExtendedTypography
              variant="caption"
              color="primary.textDisabledColor"
            >
              {timeAgo}
            </ExtendedTypography>
            {read ? (
              <VaporIcon icon={faCircle} size="12px" />
            ) : (
              <VaporIcon icon={faCircleTwo} size="12px" color="interactive" />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};
